import api from './api';
import { KpiSimpleDto, VisitasPlanIndicadoresDto } from '../types/visitas';

/** Máximo permitido por PedidosVenta, RemitosVenta y Tesorería/recibos. */
const API_PAGE_SIZE = 20;

const toSafeNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export const planFechaToIsoDay = (fecha: string): string => {
  if (!fecha) return '';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(fecha.trim());
  if (match) return match[1];
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return fecha.slice(0, 10);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const normalizePaged = <T,>(data: unknown): { items: T[]; totalPages: number } => {
  const raw = (data || {}) as Record<string, unknown>;
  const items = (raw.items ?? raw.Items ?? []) as T[];
  const totalPages = Math.max(
    1,
    Number(raw.totalPages ?? raw.TotalPages ?? 1),
  );
  return {
    items: Array.isArray(items) ? items : [],
    totalPages,
  };
};

interface ReciboListItem {
  importeTotal?: number;
  nroVendedor?: number | null;
  usuario?: string;
}

interface PedidoListItem {
  importeTotal?: number;
  nroVendedor?: number;
  usuario?: string;
  nroFormulario?: number;
  NroFormulario?: number;
  detalles?: PedidoDetalleItem[];
  Detalles?: PedidoDetalleItem[];
}

interface PedidoDetalleItem {
  nroItem?: number;
  NroItem?: number;
  cantidad?: number;
  Cantidad?: number;
  nroFormularioMaster?: number;
  NroFormularioMaster?: number;
  nroItemMaster?: number;
  NroItemMaster?: number;
}

interface RemitoListItem {
  nroVendedor?: number;
  usuario?: string;
  detalles?: PedidoDetalleItem[];
  Detalles?: PedidoDetalleItem[];
}

const getPedidoDetalles = (pedido: PedidoListItem): PedidoDetalleItem[] =>
  pedido.detalles ?? pedido.Detalles ?? [];

/** Igual que mobile: cantidades entregadas por pedido-ítem (nroFormularioMaster + nroItemMaster). */
const buildDeliveredByMasterItemMap = (remitos: RemitoListItem[]): Map<string, number> => {
  const delivered = new Map<string, number>();
  remitos.forEach((remito) => {
    const detalles = remito.detalles ?? remito.Detalles ?? [];
    detalles.forEach((detalle) => {
      const masterPedido = toSafeNumber(
        detalle.nroFormularioMaster ?? detalle.NroFormularioMaster,
      );
      const masterItem = toSafeNumber(detalle.nroItemMaster ?? detalle.NroItemMaster);
      const cantidad = toSafeNumber(detalle.cantidad ?? detalle.Cantidad);
      if (masterPedido <= 0 || masterItem <= 0 || cantidad <= 0) return;
      const key = `${masterPedido}-${masterItem}`;
      delivered.set(key, (delivered.get(key) ?? 0) + cantidad);
    });
  });
  return delivered;
};

const isPedidoCompletamenteEntregado = (
  pedidoId: number,
  detalles: PedidoDetalleItem[],
  deliveredByMasterItem: Map<string, number>,
): boolean => {
  if (detalles.length === 0) return false;
  return detalles.every((detalle) => {
    const nroItem = toSafeNumber(detalle.nroItem ?? detalle.NroItem);
    const cantidadPedida = toSafeNumber(detalle.cantidad ?? detalle.Cantidad);
    if (nroItem <= 0 || cantidadPedida <= 0) return true;
    const entregado = deliveredByMasterItem.get(`${pedidoId}-${nroItem}`) ?? 0;
    return entregado >= cantidadPedida;
  });
};

const fetchAllPages = async <T,>(
  fetchPage: (page: number) => Promise<{ items: T[]; totalPages: number }>,
): Promise<T[]> => {
  const all: T[] = [];
  let page = 1;
  let totalPages = 1;
  do {
    const { items, totalPages: tp } = await fetchPage(page);
    all.push(...items);
    totalPages = tp;
    page += 1;
  } while (page <= totalPages);
  return all;
};

const matchesVendedor = (
  item: { nroVendedor?: number | null; usuario?: string | null },
  nroVendedor: number,
  usuario?: string,
): boolean => {
  const usuarioLower = usuario?.trim().toLowerCase();
  if (usuarioLower) {
    return String(item.usuario || '').trim().toLowerCase() === usuarioLower;
  }
  return Number(item.nroVendedor) === nroVendedor;
};

const buildVendedorQueryParams = (
  nroVendedor: number,
  usuario?: string,
): Record<string, string> => {
  const usuarioTrim = usuario?.trim();
  if (usuarioTrim) {
    return { Usuario: usuarioTrim };
  }
  if (nroVendedor > 0) {
    return { NroVendedor: String(nroVendedor) };
  }
  return {};
};

const fetchAllRecibos = async (
  isoDay: string,
  nroVendedor: number,
  usuario?: string,
): Promise<ReciboListItem[]> => {
  const all: ReciboListItem[] = [];
  const vendedorParams = buildVendedorQueryParams(nroVendedor, usuario);
  let page = 1;
  let totalPages = 1;

  do {
    const response = await api.get('/api/Tesoreria/recibos', {
      params: {
        FechaDesde: isoDay,
        FechaHasta: isoDay,
        PageNumber: page,
        PageSize: API_PAGE_SIZE,
        ...vendedorParams,
      },
    });
    const { items, totalPages: tp } = normalizePaged<ReciboListItem>(response.data);
    all.push(...items.filter((r) => matchesVendedor(r, nroVendedor, usuario)));
    totalPages = tp;
    page += 1;
  } while (page <= totalPages);

  return all;
};

const sumPedidosVenta = async (
  isoDay: string,
  nroVendedor: number,
  usuario?: string,
): Promise<number> => {
  let total = 0;
  const vendedorParams = buildVendedorQueryParams(nroVendedor, usuario);
  let page = 1;
  let totalPages = 1;

  do {
    const response = await api.get('/api/PedidosVenta', {
      params: {
        FechaDesde: isoDay,
        FechaHasta: isoDay,
        PageNumber: page,
        PageSize: API_PAGE_SIZE,
        ...vendedorParams,
      },
    });
    const { items, totalPages: tp } = normalizePaged<PedidoListItem>(response.data);
    for (const pedido of items) {
      if (matchesVendedor(pedido, nroVendedor, usuario)) {
        total += toSafeNumber(pedido.importeTotal);
      }
    }
    totalPages = tp;
    page += 1;
  } while (page <= totalPages);

  return total;
};

/**
 * Entregas alineadas a mobile (entregasService):
 * - Pedidos del día del plan (con detalle).
 * - Remitos del vendedor sin filtrar por fecha (el remito puede ser otro día).
 * - Entregado = pedido con todos sus ítems cubiertos por remitos (master/item).
 */
const countEntregas = async (
  isoDay: string,
  nroVendedor: number,
  usuario?: string,
): Promise<{ entregados: number; pendientes: number; total: number }> => {
  const vendedorParams = buildVendedorQueryParams(nroVendedor, usuario);

  const pedidosDelDia = await fetchAllPages<PedidoListItem>(async (page) => {
    const response = await api.get('/api/PedidosVenta', {
      params: {
        FechaDesde: isoDay,
        FechaHasta: isoDay,
        PageNumber: page,
        PageSize: API_PAGE_SIZE,
        ...vendedorParams,
      },
    });
    const { items, totalPages } = normalizePaged<PedidoListItem>(response.data);
    return {
      items: items.filter((p) => matchesVendedor(p, nroVendedor, usuario)),
      totalPages,
    };
  });

  const remitosVendedor = await fetchAllPages<RemitoListItem>(async (page) => {
    const response = await api.get('/api/RemitosVenta', {
      params: {
        PageNumber: page,
        PageSize: API_PAGE_SIZE,
        ...vendedorParams,
      },
    });
    const { items, totalPages } = normalizePaged<RemitoListItem>(response.data);
    return {
      items: items.filter((r) => matchesVendedor(r, nroVendedor, usuario)),
      totalPages,
    };
  });

  const deliveredByMasterItem = buildDeliveredByMasterItemMap(remitosVendedor);

  let entregados = 0;
  let pendientes = 0;

  pedidosDelDia.forEach((pedido) => {
    const pedidoId = toSafeNumber(pedido.nroFormulario ?? pedido.NroFormulario);
    const detalles = getPedidoDetalles(pedido);
    if (pedidoId <= 0 || detalles.length === 0) return;

    if (isPedidoCompletamenteEntregado(pedidoId, detalles, deliveredByMasterItem)) {
      entregados += 1;
    } else {
      pendientes += 1;
    }
  });

  return { entregados, pendientes, total: entregados + pendientes };
};

export interface PlanMoneyMetrics {
  cobranzas: KpiSimpleDto;
  ventas: KpiSimpleDto;
  entregas: KpiSimpleDto;
}

export const loadPlanMoneyMetrics = async (
  fecha: string,
  nroVendedor: number,
  usuario?: string,
): Promise<PlanMoneyMetrics> => {
  const isoDay = planFechaToIsoDay(fecha);
  if (!isoDay || !Number.isFinite(nroVendedor) || nroVendedor <= 0) {
    return {
      cobranzas: { total: 0, avancePorcentaje: 0 },
      ventas: { total: 0, avancePorcentaje: 0 },
      entregas: { total: 0, avancePorcentaje: 0 },
    };
  }

  try {
    const [recibos, montoVentas, entregasCounts] = await Promise.all([
      fetchAllRecibos(isoDay, nroVendedor, usuario),
      sumPedidosVenta(isoDay, nroVendedor, usuario),
      countEntregas(isoDay, nroVendedor, usuario),
    ]);

    const montoCobranzas = recibos.reduce((acc, r) => acc + toSafeNumber(r.importeTotal), 0);
    const { entregados, total: entregasTotal } = entregasCounts;
    const entregasAvance =
      entregasTotal > 0 ? Math.round((entregados / entregasTotal) * 100) : 0;

    return {
      cobranzas: { total: montoCobranzas, avancePorcentaje: 0 },
      ventas: { total: montoVentas, avancePorcentaje: 0 },
      entregas: { total: entregasTotal, avancePorcentaje: entregasAvance },
    };
  } catch (error) {
    console.warn('visitasPlanMetrics: error cargando métricas', {
      isoDay,
      nroVendedor,
      usuario,
      error,
    });
    return {
      cobranzas: { total: 0, avancePorcentaje: 0 },
      ventas: { total: 0, avancePorcentaje: 0 },
      entregas: { total: 0, avancePorcentaje: 0 },
    };
  }
};

const planMetricsKey = (fecha: string, nroVendedor: number) =>
  `${planFechaToIsoDay(fecha)}|${nroVendedor}`;

export const enrichPlanesWithMoneyMetrics = async (
  planes: VisitasPlanIndicadoresDto[],
  usuarioPorVendedor?: Map<number, string>,
): Promise<VisitasPlanIndicadoresDto[]> => {
  if (!planes.length) return planes;

  const uniqueKeys = new Map<string, { fecha: string; nroVendedor: number }>();
  planes.forEach((plan) => {
    const key = planMetricsKey(plan.fecha, plan.nroVendedor);
    if (!uniqueKeys.has(key)) {
      uniqueKeys.set(key, { fecha: plan.fecha, nroVendedor: plan.nroVendedor });
    }
  });

  const metricsByKey = new Map<string, PlanMoneyMetrics>();
  await Promise.all(
    Array.from(uniqueKeys.entries()).map(async ([key, { fecha, nroVendedor }]) => {
      const usuario = usuarioPorVendedor?.get(nroVendedor);
      const metrics = await loadPlanMoneyMetrics(fecha, nroVendedor, usuario);
      metricsByKey.set(key, metrics);
    }),
  );

  return planes.map((plan) => {
    const metrics = metricsByKey.get(planMetricsKey(plan.fecha, plan.nroVendedor));
    if (!metrics) return plan;
    return {
      ...plan,
      cobranzas: metrics.cobranzas,
      ventas: metrics.ventas,
      entregas: metrics.entregas,
    };
  });
};
